import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  makeStyles,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import {
  Add,
  Check,
  DeleteOutline,
  Edit,
  FileCopy,
  PersonAdd,
  Schedule,
  Send,
  Tv,
  WhatsApp,
  Colorize,
} from "@material-ui/icons";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import ChatbotModal from "../../components/ChatbotModal";
import TicketStreamingModal from "../../components/TicketStreamingModal";
import api from "../../services/api";
import openSocket from "../../services/socket-io";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { usePrivacyCensorSettings } from "../../utils/privacyCensor";
import ColorPicker, {
  isValidHexColor,
  normalizeHexColor,
  saveCustomColor,
} from "../../components/ColorPicker";

const NO_STREAMING_CONNECTION = "no-streaming-connection";
const NO_COUNTDOWN_SEQUENCE = "no-countdown-sequence";

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    margin: 20,
    padding: theme.spacing(1.5),
    overflow: "hidden",
    display: "grid",
    gridTemplateColumns: "260px minmax(0, 1fr)",
    gap: theme.spacing(1.5),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
      overflow: "auto",
    },
  },
  sidebar: {
    borderRight: `1px solid ${theme.palette.divider}`,
    paddingRight: theme.spacing(1),
    overflow: "auto",
    [theme.breakpoints.down("sm")]: {
      borderRight: 0,
      borderBottom: `1px solid ${theme.palette.divider}`,
      paddingRight: 0,
      paddingBottom: theme.spacing(1),
      maxHeight: 220,
    },
  },
  platformButton: {
    width: "100%",
    border: "1px solid transparent",
    borderRadius: 6,
    padding: theme.spacing(1),
    display: "grid",
    gridTemplateColumns: "1fr auto",
    alignItems: "center",
    cursor: "pointer",
    background: "transparent",
    textAlign: "left",
    marginBottom: theme.spacing(0.75),
  },
  platformButtonActive: {
    background: "#f5f9ff",
    borderColor: "rgba(37,118,210,.25)",
  },
  expirationsButtonActive: {
    background: "#fff7f7",
    borderColor: "rgba(234,67,53,.28)",
  },
  platformName: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
    minWidth: 0,
  },
  platformText: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  platformActions: {
    display: "flex",
    alignItems: "center",
  },
  colorAdornment: {
    width: 20,
    height: 20,
    borderRadius: 4,
    border: "1px solid rgba(0, 0, 0, 0.18)",
  },
  content: {
    overflow: "auto",
    paddingRight: theme.spacing(0.5),
    minWidth: 0,
  },
  toolbar: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    marginBottom: theme.spacing(1.5),
  },
  accountCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
    overflowX: "auto",
    overflowY: "visible",
    minWidth: 0,
  },
  profileRows: {
    display: "grid",
    gap: theme.spacing(1),
    minWidth: 980,
    [theme.breakpoints.down("sm")]: {
      minWidth: 0,
    },
  },
  profileRow: {
    display: "grid",
    gridTemplateColumns: "auto repeat(5, minmax(140px, 1fr)) 104px 112px",
    gap: theme.spacing(1),
    alignItems: "center",
    padding: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    "& .MuiFormControl-root, & .MuiAutocomplete-root": {
      width: "100%",
      minWidth: 0,
    },
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "auto repeat(2, minmax(150px, 1fr)) 104px 104px",
    },
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  rowChip: {
    justifySelf: "stretch",
    minWidth: 96,
  },
  rowActions: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    minWidth: 0,
    whiteSpace: "nowrap",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "flex-start",
    },
  },
  quickDateButtons: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginTop: theme.spacing(1),
  },
  clickableChip: {
    cursor: "pointer",
  },
  countdownSummary: {
    display: "grid",
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.5),
  },
  countdownCard: {
    padding: theme.spacing(1),
    border: "1px solid rgba(0,0,0,.1)",
  },
  countdownCardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(1),
  },
  countdownCardActions: {
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
  },
  expirationBoard: {
    display: "grid",
    gridAutoFlow: "column",
    gridAutoColumns: "minmax(280px, 1fr)",
    gap: theme.spacing(1.5),
    overflowX: "auto",
    paddingBottom: theme.spacing(1),
    ...theme.scrollbarStyles,
  },
  expirationColumn: {
    display: "flex",
    flexDirection: "column",
    minHeight: 520,
    maxHeight: "calc(100vh - 220px)",
    border: "1px solid rgba(0, 0, 0, 0.12)",
    borderRadius: 8,
    backgroundColor: theme.palette.background.paper,
    overflow: "hidden",
  },
  expirationColorBar: {
    height: 5,
    flex: "0 0 auto",
  },
  expirationHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1.25),
    borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
  },
  expirationTitle: {
    flex: 1,
    fontWeight: 700,
  },
  expirationBody: {
    flex: 1,
    padding: theme.spacing(1.25),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  expirationCard: {
    padding: theme.spacing(1.25),
    marginBottom: theme.spacing(1),
    border: "1px solid rgba(0, 0, 0, 0.10)",
    borderRadius: 6,
    backgroundColor: theme.palette.background.paper,
  },
  expirationCardHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(1),
  },
  expirationMeta: {
    display: "grid",
    gap: 2,
    marginTop: theme.spacing(0.5),
  },
  expirationActions: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
  },
  emptyStage: {
    color: theme.palette.text.secondary,
    textAlign: "center",
    padding: theme.spacing(2),
  },
  censoredText: {
    filter: "blur(4px)",
    userSelect: "none",
  },
  censoredPhoto: {
    filter: "blur(6px)",
  },
}));

const EXPIRATIONS_VIEW = "__streaming_expirations__";

const expirationColumns = [
  {
    key: "expired",
    title: "Vencidos",
    color: "#9f44dc",
    background: "#faf5ff",
    border: "rgba(159,68,220,.35)",
  },
  {
    key: "today",
    title: "Vencen hoy",
    color: "#ea4335",
    background: "#fff5f4",
    border: "rgba(234,67,53,.35)",
  },
  {
    key: "tomorrow",
    title: "Vencen mañana",
    color: "#4285f4",
    background: "#f3f7ff",
    border: "rgba(66,133,244,.35)",
  },
];

const initialPlatformForm = {
  id: null,
  name: "",
  icon: "TV",
  color: "#E8F3FF",
  status: "active",
  connectionId: "",
  countdowns: [
    { value: 3, unit: "days", sequenceId: "" },
    { value: 2, unit: "days", sequenceId: "" },
    { value: 1, unit: "days", sequenceId: "" },
  ],
};

const streamingSequenceVariables = [
  { label: "Resumen de streaming", value: "{{resumen_acceso}}" },
];

const initialAccountForm = {
  id: null,
  email: "",
  password: "",
  profiles: [{ name: "Perfil 1", pin: "" }],
};

const createInitialAccountForm = () => ({
  ...initialAccountForm,
  profiles: initialAccountForm.profiles.map(profile => ({ ...profile })),
});

const normalizeAccountDraft = account => ({
  id: account?.id || null,
  email: account?.email || "",
  password: account?.password || "",
  profiles: (account?.profiles?.length ? account.profiles : initialAccountForm.profiles).map(profile => ({
    id: profile.id,
    name: profile.name || "",
    pin: profile.pin || "",
  })),
});

const getActiveAssignment = profile =>
  (profile.assignments || []).find(item => ["active", "assigned"].includes(item.status)) ||
  null;

const getActiveAccountAssignment = account =>
  (account.assignments || []).find(item => ["active", "assigned"].includes(item.status)) ||
  null;

const streamingReminderStatusLabels = {
  pending: "Pendiente",
  processing: "Procesando",
  sent: "Enviado",
  failed: "Fallido",
};

const formatStreamingReminderStatus = status =>
  streamingReminderStatusLabels[status] || status || "";

const formatDate = value => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("es-CR");
};

const formatDateTime = (value, timeZone = "") => {
  if (!value) return "-";
  const options = {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    ...(timeZone ? { timeZone } : {}),
  };

  try {
    return new Date(value).toLocaleString("es-CR", options);
  } catch {
    return new Date(value).toLocaleString("es-CR", {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  }
};

const getDateTimeParts = (value, timeZone = "") => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const options = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    ...(timeZone ? { timeZone } : {}),
  };

  try {
    const parts = new Intl.DateTimeFormat("en-US", options).formatToParts(date);
    const values = parts.reduce((acc, part) => {
      if (part.type !== "literal") acc[part.type] = part.value;
      return acc;
    }, {});
    const hour = values.hour === "24" ? "00" : values.hour;
    return `${values.year}-${values.month}-${values.day}T${hour}:${values.minute}`;
  } catch {
    return date.toISOString().slice(0, 16);
  }
};

const toDateTimeInput = (value, timeZone = "") => getDateTimeParts(value, timeZone);

const nowDateTimeInput = () => {
  const date = new Date();
  date.setSeconds(0, 0);
  const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return offsetDate.toISOString().slice(0, 16);
};

const getDaysLeft = assignment => {
  if (!assignment?.endsAt) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const endDate = new Date(assignment.endsAt);
  endDate.setHours(0, 0, 0, 0);
  if (Number.isNaN(endDate.getTime())) return null;
  return Math.ceil((endDate.getTime() - today.getTime()) / 86400000);
};

const getAssignmentChipProps = assignment => {
  if (!assignment) {
    return {
      label: "Disponible",
      style: { backgroundColor: "#fdab3d", color: "#fff" },
    };
  }

  const daysLeft = getDaysLeft(assignment);
  if (daysLeft === null) return { label: "Sin vencimiento", color: "primary" };
  if (daysLeft < 0) return { label: `${daysLeft} dias`, style: { backgroundColor: "#9f44dc", color: "#fff" } };
  if (daysLeft === 0) return { label: "0 dias", style: { backgroundColor: "#ea4335", color: "#fff" } };
  if (daysLeft === 1) return { label: "1 dia", style: { backgroundColor: "#4285f4", color: "#fff" } };
  return { label: `${daysLeft} dias`, color: "primary" };
};

const addDaysToDateTimeInput = (value, days) => {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return "";
  date.setDate(date.getDate() + days);
  date.setSeconds(0, 0);
  const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return offsetDate.toISOString().slice(0, 16);
};

const normalizeFetchedAccounts = accounts =>
  (accounts || []).map(account => ({
    ...account,
    assignments: [...(account.assignments || [])].sort((a, b) => Number(b.id || 0) - Number(a.id || 0)),
    profiles: [...(account.profiles || [])]
      .sort((a, b) => Number(a.id || 0) - Number(b.id || 0))
      .map(profile => ({
        ...profile,
        assignments: [...(profile.assignments || [])].sort((a, b) => Number(b.id || 0) - Number(a.id || 0)),
      })),
  }));

const Streaming = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const privacyCensorSettings = usePrivacyCensorSettings(user?.id);
  const [platforms, setPlatforms] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [sequences, setSequences] = useState([]);
  const [connections, setConnections] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [selectedPlatformId, setSelectedPlatformId] = useState(EXPIRATIONS_VIEW);
  const [platformModalOpen, setPlatformModalOpen] = useState(false);
  const [bulkAssignModalOpen, setBulkAssignModalOpen] = useState(false);
  const [platformForm, setPlatformForm] = useState(initialPlatformForm);
  const [platformColorPickerOpen, setPlatformColorPickerOpen] = useState(false);
  const [draftAccounts, setDraftAccounts] = useState({});
  const [assignmentDrafts, setAssignmentDrafts] = useState({});
  const [selectedItems, setSelectedItems] = useState({});
  const [bulkForm, setBulkForm] = useState({
    contact: null,
    startsAt: nowDateTimeInput(),
    endsAt: "",
  });
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmSaveKey, setConfirmSaveKey] = useState(null);
  const [confirmAssignment, setConfirmAssignment] = useState(null);
  const [countdownAssignment, setCountdownAssignment] = useState(null);
  const [dateAssignment, setDateAssignment] = useState(null);
  const [streamingModalTicket, setStreamingModalTicket] = useState(null);
  const [sequenceModalOpen, setSequenceModalOpen] = useState(false);
  const [sequenceModalId, setSequenceModalId] = useState(null);
  const [sequenceCountdownIndex, setSequenceCountdownIndex] = useState(null);

  const selectedPlatform = useMemo(
    () =>
      selectedPlatformId === EXPIRATIONS_VIEW
        ? null
        : platforms.find(platform => String(platform.id) === String(selectedPlatformId)),
    [platforms, selectedPlatformId]
  );

  const platformById = useMemo(
    () =>
      platforms.reduce((acc, platform) => {
        acc[platform.id] = platform;
        return acc;
      }, {}),
    [platforms]
  );

  const isExpirationsView = selectedPlatformId === EXPIRATIONS_VIEW;

  const newAccountDraft = draftAccounts.new || createInitialAccountForm();

  const fetchPlatforms = async () => {
    try {
      const { data } = await api.get("/streaming/platforms");
      const nextPlatforms = data.platforms || [];
      setPlatforms(nextPlatforms);
      setSelectedPlatformId(prev => prev || EXPIRATIONS_VIEW);
    } catch (err) {
      toastError(err);
    }
  };

  const fetchAccounts = async platformId => {
    if (!platformId) {
      setAccounts([]);
      return;
    }
    try {
      const { data } = await api.get("/streaming/accounts", {
        params: platformId === EXPIRATIONS_VIEW ? {} : { platformId },
      });
      setAccounts(normalizeFetchedAccounts(data.accounts));
    } catch (err) {
      toastError(err);
    }
  };

  const fetchConnections = async () => {
    try {
      const { data } = await api.get("/connections");
      setConnections(data || []);
    } catch {
      setConnections([]);
    }
  };

  const loadSequences = async () => {
    const loadedSequences = [];
    let pageNumber = 1;
    let hasMore = true;

    while (hasMore) {
      const { data } = await api.get("/chatbot/sequences", {
        params: { searchParam: "", pageNumber },
      });
      loadedSequences.push(...(data.sequences || []));
      hasMore = Boolean(data.hasMore);
      pageNumber += 1;
    }

    setSequences(loadedSequences);
    return loadedSequences;
  };

  const fetchContacts = async (searchParam = "") => {
    try {
      const { data } = await api.get("/contacts", {
        params: { searchParam, pageNumber: 1, rowsPerPage: 20 },
      });
      setContacts(data.contacts || []);
    } catch {
      setContacts([]);
    }
  };

  const getContactLabel = option =>
    option
      ? privacyCensorSettings.contactNames
        ? "Contacto privado"
        : `${option.name || ""}${option.number ? ` - ${option.number}` : ""}`
      : "";

  const getContactOptions = selectedContact => {
    if (!selectedContact?.id) return contacts;
    const exists = contacts.some(contact => String(contact.id) === String(selectedContact.id));
    return exists ? contacts : [selectedContact, ...contacts];
  };

  const renderContactOption = option => (
    <>
      <Avatar
        src={privacyCensorSettings.contactPhotos ? undefined : option?.profilePicUrl}
        className={privacyCensorSettings.contactPhotos ? classes.censoredPhoto : undefined}
        style={{ marginRight: 8 }}
      />
      <div>
        <Typography
          variant="body2"
          className={privacyCensorSettings.contactNames ? classes.censoredText : undefined}
        >
          {privacyCensorSettings.contactNames ? "Contacto privado" : option?.name || "-"}
        </Typography>
        <Typography
          variant="caption"
          color="textSecondary"
          className={privacyCensorSettings.contactNames ? classes.censoredText : undefined}
        >
          {privacyCensorSettings.contactNames ? "Telefono privado" : option?.number || ""}
        </Typography>
      </div>
    </>
  );

  useEffect(() => {
    fetchPlatforms();
    fetchConnections();
    fetchContacts();
    loadSequences().catch(toastError);
  }, []);

  useEffect(() => {
    fetchAccounts(selectedPlatformId);
  }, [selectedPlatformId]);

  useEffect(() => {
    const socket = openSocket();
    socket.on("streaming", () => {
      fetchPlatforms();
      fetchAccounts(selectedPlatformId);
    });
    return () => socket.disconnect();
  }, [selectedPlatformId]);

  const openPlatformModal = platform => {
    setPlatformForm(
      platform
        ? {
            id: platform.id,
            name: platform.name || "",
            icon: platform.icon || "TV",
            color: platform.color || "#E8F3FF",
            status: platform.status || "active",
            connectionId: platform.connectionId || "",
            countdowns: platform.countdowns?.length
              ? platform.countdowns
              : initialPlatformForm.countdowns,
          }
        : initialPlatformForm
    );
    setPlatformModalOpen(true);
  };

  const savePlatform = async () => {
    try {
      const payload = {
        ...platformForm,
        connectionId: platformForm.connectionId || null,
      };
      const { data } = platformForm.id
        ? await api.put(`/streaming/platforms/${platformForm.id}`, payload)
        : await api.post("/streaming/platforms", payload);
      await fetchPlatforms();
      setSelectedPlatformId(data.id);
      setPlatformModalOpen(false);
      toast.success("Plataforma guardada.");
    } catch (err) {
      toastError(err);
    }
  };

  const formatSequenceOption = sequence => `ID: ${sequence.id} - ${sequence.name}`;

  const openCreateSequence = index => {
    setSequenceCountdownIndex(index);
    setSequenceModalId(null);
    setSequenceModalOpen(true);
  };

  const openEditSequence = (index, sequenceId) => {
    if (!sequenceId) return;
    setSequenceCountdownIndex(index);
    setSequenceModalId(sequenceId);
    setSequenceModalOpen(true);
  };

  const handleSequenceSaved = async savedSequence => {
    const savedSequenceId = savedSequence?.id || sequenceModalId;
    const nextSequences = await loadSequences();

    if (
      savedSequence &&
      savedSequenceId &&
      !nextSequences.some(sequence => String(sequence.id) === String(savedSequenceId))
    ) {
      setSequences(prev => [...prev, savedSequence]);
    }

    if (sequenceCountdownIndex !== null && savedSequenceId) {
      const countdowns = [...(platformForm.countdowns || [])];
      countdowns[sequenceCountdownIndex] = {
        ...countdowns[sequenceCountdownIndex],
        sequenceId: String(savedSequenceId),
      };
      setPlatformForm(prev => ({ ...prev, countdowns }));
    }

    setSequenceModalOpen(false);
    setSequenceModalId(null);
    setSequenceCountdownIndex(null);
  };

  const handleSequenceModalClose = () => {
    setSequenceModalOpen(false);
    setSequenceModalId(null);
    setSequenceCountdownIndex(null);
  };

  const updateDraftAccount = (key, patch, sourceAccount = null) => {
    setDraftAccounts(prev => ({
      ...prev,
      [key]: {
        ...(prev[key] || normalizeAccountDraft(sourceAccount)),
        ...patch,
      },
    }));
  };

  const updateDraftProfile = (key, index, patch, sourceAccount = null) => {
    setDraftAccounts(prev => {
      const account = prev[key] || normalizeAccountDraft(sourceAccount);
      const profiles = [...(account.profiles || [])];
      profiles[index] = { ...profiles[index], ...patch };
      return { ...prev, [key]: { ...account, profiles } };
    });
  };

  const saveDraftAccount = async key => {
    if (!key || !selectedPlatformId) return;
    const draft = key === "new" ? draftAccounts.new || createInitialAccountForm() : draftAccounts[key];
    if (!draft) return;
    try {
      const payload = {
        ...draft,
        platformId: selectedPlatformId,
        profiles: (draft.profiles || []).filter(profile => profile.name?.trim()),
      };
      draft.id
        ? await api.put(`/streaming/accounts/${draft.id}`, payload)
        : await api.post("/streaming/accounts", payload);
      setDraftAccounts(prev => {
        const next = { ...prev };
        if (key === "new") next.new = createInitialAccountForm();
        else delete next[key];
        return next;
      });
      await fetchAccounts(selectedPlatformId);
      toast.success("Cuenta guardada.");
    } catch (err) {
      toastError(err);
    }
  };

  const cancelDraftAccount = key => {
    setDraftAccounts(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const assignmentDraftKey = (targetType, targetId) => `${targetType}-${targetId}`;

  const getAssignmentDraft = (targetType, targetId, assignment) => (
    assignmentDrafts[assignmentDraftKey(targetType, targetId)] || {
      contact: assignment?.contact || null,
      startsAt: toDateTimeInput(assignment?.startsAt || new Date(), assignment?.connection?.timeZone),
      endsAt: toDateTimeInput(assignment?.endsAt, assignment?.connection?.timeZone),
    }
  );

  const updateAssignmentDraft = (targetType, targetId, patch, assignment) => {
    const key = assignmentDraftKey(targetType, targetId);
    setAssignmentDrafts(prev => ({
      ...prev,
      [key]: {
        ...(prev[key] || {
          contact: assignment?.contact || null,
          startsAt: toDateTimeInput(assignment?.startsAt || new Date(), assignment?.connection?.timeZone),
          endsAt: toDateTimeInput(assignment?.endsAt, assignment?.connection?.timeZone),
        }),
        ...patch,
      },
    }));
  };

  const requestAssignmentSave = (target, targetType, assignment, draft = null) => {
    if (!target?.id) return;
    setConfirmAssignment({ target, targetType, assignment, draft });
  };

  const requestDateAssignmentSave = (target, targetType, assignment, draft) => {
    if (!target?.id) return;
    setDateAssignment({ target, targetType, assignment, draft });
  };

  const requestAccountSave = key => {
    if (key && key !== "new" && draftAccounts[key]) setConfirmSaveKey(key);
  };

  const saveInlineAssignment = async (target, targetType, assignment, draftOverride = null) => {
    const draft = draftOverride || getAssignmentDraft(targetType, target.id, assignment);
    const contactId = draft.contact?.id || null;
    if (!contactId && !draft.endsAt) {
      if (assignment) {
        targetType === "account"
          ? await releaseAccount(target)
          : await releaseProfile(target);
      }
      setAssignmentDrafts(prev => {
        const next = { ...prev };
        delete next[assignmentDraftKey(targetType, target.id)];
        return next;
      });
      return;
    }
    if (!draft.endsAt) {
      toast.error("Selecciona una fecha final.");
      return;
    }
    try {
      const endpoint = targetType === "account"
        ? `/streaming/accounts/${target.id}/assign`
        : `/streaming/profiles/${target.id}/assign`;
      await api.post(endpoint, {
        contactId,
        startsAt: draft.startsAt,
        endsAt: draft.endsAt,
        connectionId: selectedPlatform?.connectionId || null,
      });
      setAssignmentDrafts(prev => {
        const next = { ...prev };
        delete next[assignmentDraftKey(targetType, target.id)];
        return next;
      });
      await fetchAccounts(selectedPlatformId);
      toast.success("Asignacion guardada.");
    } catch (err) {
      toastError(err);
    }
  };

  const saveBulkAssignment = async () => {
    const items = Object.values(selectedItems);
    if (!items.length) return;
    try {
      await api.post("/streaming/assignments/bulk", {
        items,
        contactId: bulkForm.contact?.id,
        startsAt: bulkForm.startsAt,
        endsAt: bulkForm.endsAt,
        connectionId: selectedPlatform?.connectionId || null,
      });
      setSelectedItems({});
      setBulkAssignModalOpen(false);
      await fetchAccounts(selectedPlatformId);
      toast.success("Asignaciones guardadas.");
    } catch (err) {
      toastError(err);
    }
  };

  const releaseProfile = async profile => {
    try {
      await api.post(`/streaming/profiles/${profile.id}/release`);
      await fetchAccounts(selectedPlatformId);
      toast.success("Perfil liberado.");
    } catch (err) {
      toastError(err);
    }
  };

  const releaseAccount = async account => {
    try {
      await api.post(`/streaming/accounts/${account.id}/release`);
      await fetchAccounts(selectedPlatformId);
      toast.success("Cuenta liberada.");
    } catch (err) {
      toastError(err);
    }
  };

  const buildAccessSummary = (account, profile, assignment) => {
    const platform = platformById[account?.platformId] || selectedPlatform;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const endDate = assignment?.endsAt ? new Date(assignment.endsAt) : null;
    endDate?.setHours(0, 0, 0, 0);
    const daysLeft = endDate
      ? Math.ceil((endDate.getTime() - today.getTime()) / 86400000)
      : "";

    return [
      `*${platform?.name || "Streaming"}*`,
      "",
      account.email ? `Correo: ${account.email}` : "",
      account.password ? `Contrasena: ${account.password}` : "",
      profile?.name ? `Perfil: ${profile.name}` : "",
      profile?.pin ? `PIN: ${profile.pin}` : "",
      assignment?.startsAt ? `Inicia: ${formatDate(assignment.startsAt)}` : "",
      assignment?.endsAt ? `Finaliza: ${formatDate(assignment.endsAt)}` : "",
      assignment?.endsAt ? `Quedan: ${daysLeft} dias` : "",
    ].filter(Boolean).join("\n");
  };

  const copyAccess = async (account, profile, assignment) => {
    try {
      await navigator.clipboard.writeText(buildAccessSummary(account, profile, assignment));
      toast.success("Datos copiados.");
    } catch {
      toast.error("No se pudo copiar al portapapeles.");
    }
  };

  const sendAssignment = async assignment => {
    try {
      await api.post(`/streaming/assignments/${assignment.id}/send`);
      toast.success("Datos enviados al chat.");
    } catch (err) {
      toastError(err);
    }
  };

  const openAssignmentTicket = async assignment => {
    if (!assignment?.id) return;

    const existingTicketId = assignment.ticketId || assignment.ticket?.id;
    if (existingTicketId) {
      history.push(`/tickets/${existingTicketId}`);
      return;
    }

    try {
      const { data } = await api.get(`/streaming/assignments/${assignment.id}/ticket`);
      if (data?.ticketId) {
        history.push(`/tickets/${data.ticketId}`);
        return;
      }
      toast.error("No se pudo abrir el ticket.");
    } catch (err) {
      toastError(err);
    }
  };

  const toggleSelection = item => {
    const key = `${item.type}-${item.id}`;
    setSelectedItems(prev => {
      const next = { ...prev };
      if (next[key]) delete next[key];
      else next[key] = item;
      return next;
    });
  };

  const openStreamingContactModal = item => {
    const contact = item.assignment?.contact;
    const contactId = item.assignment?.contactId || contact?.id;

    if (!contactId) {
      toast.error("Esta asignacion no tiene contacto asociado.");
      return;
    }

    setStreamingModalTicket({
      id: `streaming-assignment-${item.assignment.id}`,
      contactId,
      contact: contact || { id: contactId },
      connectionId: item.assignment?.connectionId || item.platform?.connectionId || "",
    });
  };

  const expirationGroups = useMemo(() => {
    const groups = {
      expired: [],
      today: [],
      tomorrow: [],
    };

    accounts.forEach(account => {
      const platform = platformById[account.platformId];
      const accountAssignment = getActiveAccountAssignment(account);

      if (accountAssignment) {
        const daysLeft = getDaysLeft(accountAssignment);
        const groupKey = daysLeft < 0 ? "expired" : daysLeft === 0 ? "today" : daysLeft === 1 ? "tomorrow" : "";
        if (groupKey) {
          groups[groupKey].push({
            key: `account-${account.id}`,
            typeLabel: "Cuenta completa",
            title: account.email || "Cuenta sin correo",
            pin: "",
            account,
            profile: null,
            platform,
            assignment: accountAssignment,
            daysLeft,
          });
        }
      }

      (account.profiles || []).forEach(profile => {
        const profileAssignment = getActiveAssignment(profile);
        if (!profileAssignment) return;

        const daysLeft = getDaysLeft(profileAssignment);
        const groupKey = daysLeft < 0 ? "expired" : daysLeft === 0 ? "today" : daysLeft === 1 ? "tomorrow" : "";
        if (!groupKey) return;

        groups[groupKey].push({
          key: `profile-${profile.id}`,
          typeLabel: "Perfil",
          title: profile.name || "Perfil sin nombre",
          pin: profile.pin || "",
          account,
          profile,
          platform,
          assignment: profileAssignment,
          daysLeft,
        });
      });
    });

    Object.keys(groups).forEach(key => {
      groups[key].sort((a, b) => {
        if (a.daysLeft !== b.daysLeft) return a.daysLeft - b.daysLeft;
        return new Date(a.assignment.endsAt || 0).getTime() - new Date(b.assignment.endsAt || 0).getTime();
      });
    });

    return groups;
  }, [accounts, platformById]);

  const renderExpirationCard = (item, column) => {
    const chipProps = getAssignmentChipProps(item.assignment);
    const contact = item.assignment?.contact;

    return (
      <Paper
        key={item.key}
        className={classes.expirationCard}
        elevation={0}
        style={{
          borderColor: column.border,
          borderLeft: `4px solid ${column.color}`,
        }}
      >
        <div className={classes.expirationCardHeader}>
          <div>
            <Typography variant="subtitle2">{item.title}</Typography>
            <Typography variant="caption" color="textSecondary">
              {item.platform?.name || "Streaming"} - {item.typeLabel}
            </Typography>
          </div>
          <Chip size="small" {...chipProps} />
        </div>
        <div className={classes.expirationMeta}>
          {item.pin ? (
            <Typography variant="caption" color="textSecondary">
              PIN: {item.pin}
            </Typography>
          ) : null}
          <Typography variant="caption" color="textSecondary">
            Contacto:{" "}
            <span className={privacyCensorSettings.contactNames ? classes.censoredText : undefined}>
              {privacyCensorSettings.contactNames
                ? "Contacto privado"
                : `${contact?.name || "Sin asignar"}${
                    contact?.number ? ` - ${contact.number}` : ""
                  }`}
            </span>
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Inicia: {formatDate(item.assignment.startsAt)}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Finaliza: {formatDate(item.assignment.endsAt)}
          </Typography>
        </div>
        <div className={classes.expirationActions}>
          <Tooltip title="Ir al chat">
            <IconButton size="small" onClick={() => openAssignmentTicket(item.assignment)}>
              <WhatsApp fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Editar asignaciones">
            <IconButton size="small" onClick={() => openStreamingContactModal(item)}>
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Copiar datos">
            <IconButton size="small" onClick={() => copyAccess(item.account, item.profile, item.assignment)}>
              <FileCopy fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Enviar al chat">
            <IconButton size="small" onClick={() => sendAssignment(item.assignment)}>
              <Send fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Countdowns">
            <IconButton size="small" onClick={() => setCountdownAssignment(item.assignment)}>
              <Schedule fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      </Paper>
    );
  };

  const renderExpirationsBoard = () => (
    <div className={classes.expirationBoard}>
      {expirationColumns.map(column => {
        const items = expirationGroups[column.key] || [];
        return (
          <Paper
            key={column.key}
            className={classes.expirationColumn}
            elevation={0}
            style={{ backgroundColor: column.background, borderColor: column.border }}
          >
            <span className={classes.expirationColorBar} style={{ backgroundColor: column.color }} />
            <div
              className={classes.expirationHeader}
              style={{ borderBottomColor: column.border }}
            >
              <Typography className={classes.expirationTitle} variant="subtitle1" style={{ color: column.color }}>
                {column.title}
              </Typography>
              <Chip
                size="small"
                label={items.length}
                style={{
                  backgroundColor: "#fff",
                  color: column.color,
                  border: `1px solid ${column.border}`,
                  fontWeight: 700,
                }}
              />
            </div>
            <div className={classes.expirationBody}>
              {items.length ? (
                items.map(item => renderExpirationCard(item, column))
              ) : (
                <div className={classes.emptyStage}>Sin vencimientos</div>
              )}
            </div>
          </Paper>
        );
      })}
    </div>
  );

  return (
    <MainContainer>
      <ConfirmationModal
        title="Eliminar"
        open={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        onConfirm={async () => {
          try {
            if (confirmDelete?.type === "platform") {
              await api.delete(`/streaming/platforms/${confirmDelete.item.id}`);
              await fetchPlatforms();
              setSelectedPlatformId("");
            }
            if (confirmDelete?.type === "account") {
              await api.delete(`/streaming/accounts/${confirmDelete.item.id}`);
              await fetchAccounts(selectedPlatformId);
            }
            if (confirmDelete?.type === "profile") {
              await api.delete(`/streaming/profiles/${confirmDelete.item.id}`);
              await fetchAccounts(selectedPlatformId);
              toast.success("Perfil eliminado.");
            }
            if (confirmDelete?.type === "countdown") {
              await api.delete(`/streaming/countdowns/${confirmDelete.item.id}`);
              setCountdownAssignment(prev => prev
                ? {
                    ...prev,
                    reminders: (prev.reminders || []).filter(
                      reminder => Number(reminder.id) !== Number(confirmDelete.item.id)
                    ),
                  }
                : null);
              await fetchAccounts(selectedPlatformId);
              toast.success("Countdown eliminado.");
            }
          } catch (err) {
            toastError(err);
          }
          setConfirmDelete(null);
        }}
      >
        Esta accion no se puede deshacer.
      </ConfirmationModal>

      <ConfirmationModal
        title="Guardar cambio"
        open={Boolean(confirmSaveKey)}
        onClose={() => setConfirmSaveKey(null)}
        onConfirm={async () => {
          await saveDraftAccount(confirmSaveKey);
          setConfirmSaveKey(null);
        }}
      >
        Deseas guardar este cambio?
      </ConfirmationModal>

      <ConfirmationModal
        title="Guardar cambio"
        open={Boolean(confirmAssignment)}
        onClose={() => setConfirmAssignment(null)}
        onConfirm={async () => {
          if (confirmAssignment) {
            await saveInlineAssignment(
              confirmAssignment.target,
              confirmAssignment.targetType,
              confirmAssignment.assignment,
              confirmAssignment.draft
            );
          }
          setConfirmAssignment(null);
        }}
      >
        Deseas guardar este cambio?
      </ConfirmationModal>

      <Dialog open={Boolean(dateAssignment)} onClose={() => setDateAssignment(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Ajustar fechas</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Inicio"
                type="datetime-local"
                variant="outlined"
                margin="dense"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={dateAssignment?.draft?.startsAt || ""}
                onChange={event => setDateAssignment(prev => prev
                  ? { ...prev, draft: { ...prev.draft, startsAt: event.target.value } }
                  : prev)}
              />
            </Grid>
            <Grid item xs={12}>
              <div className={classes.quickDateButtons}>
                {[30, 60, 90].map(days => (
                  <Button
                    key={days}
                    size="small"
                    variant="outlined"
                    onClick={() => setDateAssignment(prev => prev
                      ? {
                          ...prev,
                          draft: {
                            ...prev.draft,
                            startsAt: prev.draft.startsAt || nowDateTimeInput(),
                            endsAt: addDaysToDateTimeInput(prev.draft.startsAt || nowDateTimeInput(), days),
                          },
                        }
                      : prev)}
                  >
                    {days} dias
                  </Button>
                ))}
              </div>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Finaliza"
                type="datetime-local"
                variant="outlined"
                margin="dense"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={dateAssignment?.draft?.endsAt || ""}
                onChange={event => setDateAssignment(prev => prev
                  ? { ...prev, draft: { ...prev.draft, endsAt: event.target.value } }
                  : prev)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDateAssignment(null)}>Cancelar</Button>
          <Button
            color="primary"
            variant="contained"
            onClick={async () => {
              if (dateAssignment) {
                await saveInlineAssignment(
                  dateAssignment.target,
                  dateAssignment.targetType,
                  dateAssignment.assignment,
                  dateAssignment.draft
                );
              }
              setDateAssignment(null);
            }}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <MainHeader>
        <Title>Streaming</Title>
        <MainHeaderButtonsWrapper>
          <Button variant="contained" color="primary" startIcon={<Add />} onClick={() => openPlatformModal()}>
            Plataforma
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper}>
        <aside className={classes.sidebar}>
          <Typography variant="subtitle2" gutterBottom>
            Plataformas
          </Typography>
          <div
            role="button"
            tabIndex={0}
            className={`${classes.platformButton} ${
              isExpirationsView ? classes.expirationsButtonActive : ""
            }`}
            onClick={() => {
              setSelectedItems({});
              setSelectedPlatformId(EXPIRATIONS_VIEW);
            }}
          >
            <span className={classes.platformName}>
              <Avatar style={{ backgroundColor: "#fff5f4", color: "#ea4335" }}>
                <Schedule fontSize="small" />
              </Avatar>
              <span className={classes.platformText}>Vencimientos</span>
            </span>
          </div>
          {platforms.map(platform => (
            <div
              key={platform.id}
              role="button"
              tabIndex={0}
              className={`${classes.platformButton} ${
                String(platform.id) === String(selectedPlatformId)
                  ? classes.platformButtonActive
                  : ""
              }`}
              onClick={() => {
                setSelectedItems({});
                setSelectedPlatformId(platform.id);
              }}
            >
              <span className={classes.platformName}>
                <Avatar style={{ backgroundColor: platform.color || "#E8F3FF" }}>
                  <Tv fontSize="small" />
                </Avatar>
                <span className={classes.platformText}>{platform.name}</span>
              </span>
              <span className={classes.platformActions}>
                <Tooltip title="Editar plataforma y countdowns">
                  <IconButton size="small" onClick={event => {
                    event.stopPropagation();
                    openPlatformModal(platform);
                  }}>
                    <Edit fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Eliminar plataforma">
                  <IconButton size="small" onClick={event => {
                    event.stopPropagation();
                    setConfirmDelete({ type: "platform", item: platform });
                  }}>
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </Tooltip>
              </span>
            </div>
          ))}
        </aside>

        <section className={classes.content}>
          <div className={classes.toolbar}>
            <div>
              <Typography variant="h6">
                {isExpirationsView ? "Vencimientos" : selectedPlatform?.name || "Selecciona una plataforma"}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {isExpirationsView
                  ? "Cuentas y perfiles vencidos, venciendo hoy o venciendo mañana."
                  : "Las cuentas se muestran con sus perfiles y asignaciones activas."}
              </Typography>
            </div>
            {selectedPlatform ? (
              <div>
                <Button
                  startIcon={<PersonAdd />}
                  disabled={!Object.keys(selectedItems).length}
                  onClick={() => {
                    fetchContacts();
                    setBulkAssignModalOpen(true);
                  }}
                >
                  Asignar seleccion
                </Button>
                <Button startIcon={<Schedule />} onClick={() => openPlatformModal(selectedPlatform)}>
                  Countdowns
                </Button>
              </div>
            ) : null}
          </div>

          {isExpirationsView ? renderExpirationsBoard() : null}

          {selectedPlatform ? (
            <div className={classes.accountCard}>
              <div className={classes.profileRows}>
                <div className={classes.profileRow}>
                  <Checkbox disabled />
                  <TextField label="Correo" variant="outlined" margin="dense" value={newAccountDraft.email} onChange={event => updateDraftAccount("new", { email: event.target.value })} />
                  <TextField label="Contrasena" variant="outlined" margin="dense" value={newAccountDraft.password} onChange={event => updateDraftAccount("new", { password: event.target.value })} />
                  <Autocomplete
                    disabled
                    options={contacts}
                    getOptionLabel={getContactLabel}
                    getOptionSelected={(option, value) => String(option?.id) === String(value?.id)}
                    renderOption={renderContactOption}
                    value={null}
                    renderInput={params => <TextField {...params} label="Contacto" variant="outlined" margin="dense" />}
                  />
                  <TextField disabled label="Inicio" type="datetime-local" variant="outlined" margin="dense" InputLabelProps={{ shrink: true }} value="" />
                  <TextField disabled label="Finaliza" type="datetime-local" variant="outlined" margin="dense" InputLabelProps={{ shrink: true }} value="" />
                  <Chip size="small" className={classes.rowChip} {...getAssignmentChipProps(null)} />
                  <div className={classes.rowActions}>
                    <Tooltip title="Guardar cuenta">
                      <IconButton onClick={() => setConfirmSaveKey("new")}><Check /></IconButton>
                    </Tooltip>
                    <Tooltip title="Descartar">
                      <IconButton onClick={() => cancelDraftAccount("new")}><DeleteOutline /></IconButton>
                    </Tooltip>
                  </div>
                </div>
                {(newAccountDraft.profiles || []).map((profile, index) => (
                  <div className={classes.profileRow} key={index}>
                    <Checkbox disabled />
                    <TextField label="Perfil" variant="outlined" margin="dense" value={profile.name} onChange={event => updateDraftProfile("new", index, { name: event.target.value })} />
                    <TextField label="PIN" variant="outlined" margin="dense" value={profile.pin} onChange={event => updateDraftProfile("new", index, { pin: event.target.value })} />
                    <Autocomplete
                      disabled
                      options={contacts}
                      getOptionLabel={getContactLabel}
                      getOptionSelected={(option, value) => String(option?.id) === String(value?.id)}
                      renderOption={renderContactOption}
                      value={null}
                      renderInput={params => <TextField {...params} label="Contacto" variant="outlined" margin="dense" />}
                    />
                    <TextField disabled label="Inicio" type="datetime-local" variant="outlined" margin="dense" InputLabelProps={{ shrink: true }} value="" />
                    <TextField disabled label="Finaliza" type="datetime-local" variant="outlined" margin="dense" InputLabelProps={{ shrink: true }} value="" />
                    <Chip size="small" className={classes.rowChip} {...getAssignmentChipProps(null)} />
                    <div className={classes.rowActions}>
                      <Tooltip title="Quitar perfil">
                        <IconButton size="small" onClick={() => updateDraftAccount("new", { profiles: newAccountDraft.profiles.filter((_, i) => i !== index) })}>
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </div>
                ))}
              </div>
              <Button startIcon={<Add />} onClick={() => updateDraftAccount("new", { profiles: [...newAccountDraft.profiles, { name: `Perfil ${newAccountDraft.profiles.length + 1}`, pin: "" }] })}>
                Agregar perfil
              </Button>
            </div>
          ) : null}

          {selectedPlatform ? accounts.map(account => {
            const accountKey = String(account.id);
            const draft = draftAccounts[accountKey] || normalizeAccountDraft(account);
            return (
            <div className={classes.accountCard} key={account.id}>
              <div className={classes.profileRows}>
                {(() => {
                  const assignment = getActiveAccountAssignment(account);
                  const assignmentDraft = getAssignmentDraft("account", account.id, assignment);
                  const selection = { type: "account", id: account.id };
                  return (
                    <div className={classes.profileRow}>
                      <Checkbox checked={Boolean(selectedItems[`account-${account.id}`])} onChange={() => toggleSelection(selection)} />
                      <TextField label="Correo" variant="outlined" margin="dense" value={draft.email} onBlur={() => requestAccountSave(accountKey)} onChange={event => updateDraftAccount(accountKey, { email: event.target.value }, account)} />
                      <TextField label="Contrasena" variant="outlined" margin="dense" value={draft.password} onBlur={() => requestAccountSave(accountKey)} onChange={event => updateDraftAccount(accountKey, { password: event.target.value }, account)} />
                      <Autocomplete
                        options={getContactOptions(assignmentDraft.contact)}
                        getOptionLabel={getContactLabel}
                        getOptionSelected={(option, value) => String(option?.id) === String(value?.id)}
                        renderOption={renderContactOption}
                        value={assignmentDraft.contact}
                        onOpen={() => fetchContacts()}
                        onChange={(_, value) => {
                          const nextDraft = { ...assignmentDraft, contact: value };
                          updateAssignmentDraft("account", account.id, { contact: value }, assignment);
                          requestAssignmentSave(account, "account", assignment, nextDraft);
                        }}
                        onInputChange={(_, value) => fetchContacts(value)}
                        renderInput={params => <TextField {...params} label="Contacto" variant="outlined" margin="dense" />}
                      />
                      <TextField label="Inicio" type="datetime-local" variant="outlined" margin="dense" InputLabelProps={{ shrink: true }} value={assignmentDraft.startsAt} onMouseDown={event => {
                        event.preventDefault();
                        requestDateAssignmentSave(account, "account", assignment, { ...assignmentDraft });
                      }} onChange={event => {
                        updateAssignmentDraft("account", account.id, { startsAt: event.target.value }, assignment);
                      }} />
                      <TextField label="Finaliza" type="datetime-local" variant="outlined" margin="dense" InputLabelProps={{ shrink: true }} value={assignmentDraft.endsAt} onChange={event => {
                        const nextDraft = { ...assignmentDraft, endsAt: event.target.value };
                        updateAssignmentDraft("account", account.id, { endsAt: event.target.value }, assignment);
                        requestAssignmentSave(account, "account", assignment, nextDraft);
                      }} />
                      <Chip
                        size="small"
                        {...getAssignmentChipProps(assignment)}
                        className={`${classes.rowChip} ${assignment ? classes.clickableChip : ""}`}
                        onClick={assignment ? () => setCountdownAssignment(assignment) : undefined}
                      />
                      <div className={classes.rowActions}>
                        <Tooltip title="Copiar datos">
                          <IconButton size="small" onClick={() => copyAccess(account, null, assignment)}>
                            <FileCopy fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Enviar al chat">
                          <span>
                            <IconButton size="small" disabled={!assignment?.contactId} onClick={() => sendAssignment(assignment)}>
                              <Send fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Eliminar cuenta">
                          <IconButton size="small" onClick={() => setConfirmDelete({ type: "account", item: account })}>
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </div>
                  );
                })()}
                {(draft.profiles || []).map((profile, index) => {
                  const persistedProfile = (account.profiles || []).find(item => Number(item.id) === Number(profile.id)) || profile;
                  const assignment = profile.id ? getActiveAssignment(persistedProfile) : null;
                  const assignmentDraft = profile.id ? getAssignmentDraft("profile", profile.id, assignment) : null;
                  const selection = { type: "profile", id: profile.id };
                  return (
                    <div className={classes.profileRow} key={profile.id || index}>
                      <Checkbox disabled={!profile.id} checked={Boolean(selectedItems[`profile-${profile.id}`])} onChange={() => toggleSelection(selection)} />
                      <TextField label="Perfil" variant="outlined" margin="dense" value={profile.name} onBlur={() => requestAccountSave(accountKey)} onChange={event => updateDraftProfile(accountKey, index, { name: event.target.value }, account)} />
                      <TextField label="PIN" variant="outlined" margin="dense" value={profile.pin} onBlur={() => requestAccountSave(accountKey)} onChange={event => updateDraftProfile(accountKey, index, { pin: event.target.value }, account)} />
                      <Autocomplete
                        disabled={!profile.id}
                        options={getContactOptions(assignmentDraft?.contact)}
                        getOptionLabel={getContactLabel}
                        getOptionSelected={(option, value) => String(option?.id) === String(value?.id)}
                        renderOption={renderContactOption}
                        value={assignmentDraft?.contact || null}
                        onOpen={() => fetchContacts()}
                        onChange={(_, value) => {
                          const nextDraft = {
                            startsAt: assignmentDraft?.startsAt || nowDateTimeInput(),
                            endsAt: assignmentDraft?.endsAt || "",
                            contact: value,
                          };
                          updateAssignmentDraft("profile", profile.id, { contact: value }, assignment);
                          requestAssignmentSave(persistedProfile, "profile", assignment, nextDraft);
                        }}
                        onInputChange={(_, value) => fetchContacts(value)}
                        renderInput={params => <TextField {...params} label="Contacto" variant="outlined" margin="dense" />}
                      />
                      <TextField disabled={!profile.id} label="Inicio" type="datetime-local" variant="outlined" margin="dense" InputLabelProps={{ shrink: true }} value={assignmentDraft?.startsAt || ""} onMouseDown={event => {
                        event.preventDefault();
                        requestDateAssignmentSave(persistedProfile, "profile", assignment, {
                          contact: assignmentDraft?.contact || null,
                          startsAt: assignmentDraft?.startsAt || nowDateTimeInput(),
                          endsAt: assignmentDraft?.endsAt || "",
                        });
                      }} onChange={event => {
                        updateAssignmentDraft("profile", profile.id, { startsAt: event.target.value }, assignment);
                      }} />
                      <TextField disabled={!profile.id} label="Finaliza" type="datetime-local" variant="outlined" margin="dense" InputLabelProps={{ shrink: true }} value={assignmentDraft?.endsAt || ""} onChange={event => {
                        const nextDraft = {
                          contact: assignmentDraft?.contact || null,
                          startsAt: assignmentDraft?.startsAt || nowDateTimeInput(),
                          endsAt: event.target.value,
                        };
                        updateAssignmentDraft("profile", profile.id, { endsAt: event.target.value }, assignment);
                        requestAssignmentSave(persistedProfile, "profile", assignment, nextDraft);
                      }} />
                      <Chip
                        size="small"
                        {...getAssignmentChipProps(assignment)}
                        className={`${classes.rowChip} ${assignment ? classes.clickableChip : ""}`}
                        onClick={assignment ? () => setCountdownAssignment(assignment) : undefined}
                      />
                      <div className={classes.rowActions}>
                        <Tooltip title="Copiar datos">
                          <IconButton size="small" disabled={!profile.id} onClick={() => copyAccess(account, persistedProfile, assignment)}>
                            <FileCopy fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Enviar al chat">
                          <span>
                            <IconButton size="small" disabled={!assignment?.contactId} onClick={() => sendAssignment(assignment)}>
                              <Send fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Eliminar perfil">
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (profile.id) {
                                setConfirmDelete({ type: "profile", item: persistedProfile });
                              } else {
                                updateDraftAccount(accountKey, { profiles: draft.profiles.filter((_, i) => i !== index) }, account);
                              }
                            }}
                          >
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </div>
                  );
                })}
              </div>
              <Button startIcon={<Add />} onClick={() => updateDraftAccount(accountKey, { profiles: [...draft.profiles, { name: `Perfil ${draft.profiles.length + 1}`, pin: "" }] }, account)}>
                Agregar perfil
              </Button>
            </div>
            );
          }) : null}
        </section>
      </Paper>

      <Dialog open={platformModalOpen} onClose={() => setPlatformModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{platformForm.id ? "Editar plataforma" : "Nueva plataforma"}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={5}>
              <TextField label="Nombre" variant="outlined" margin="dense" fullWidth value={platformForm.name} onChange={event => setPlatformForm({ ...platformForm, name: event.target.value })} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                label="Color"
                variant="outlined"
                margin="dense"
                fullWidth
                value={platformForm.color}
                onChange={event =>
                  setPlatformForm({ ...platformForm, color: event.target.value })
                }
                onBlur={event => {
                  if (!isValidHexColor(event.target.value)) return;
                  const color = normalizeHexColor(event.target.value);
                  setPlatformForm(prev => ({ ...prev, color }));
                  saveCustomColor(color);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <div
                        className={classes.colorAdornment}
                        style={{ backgroundColor: platformForm.color }}
                      />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <IconButton
                      size="small"
                      onClick={() => setPlatformColorPickerOpen(true)}
                    >
                      <Colorize />
                    </IconButton>
                  ),
                }}
              />
              <ColorPicker
                open={platformColorPickerOpen}
                currentColor={platformForm.color}
                handleClose={() => setPlatformColorPickerOpen(false)}
                onChange={color =>
                  setPlatformForm(prev => ({ ...prev, color }))
                }
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl variant="outlined" margin="dense" fullWidth>
                <InputLabel>Estado</InputLabel>
                <Select label="Estado" value={platformForm.status} onChange={event => setPlatformForm({ ...platformForm, status: event.target.value })}>
                  <MenuItem value="active">Activa</MenuItem>
                  <MenuItem value="inactive">Inactiva</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl variant="outlined" margin="dense" fullWidth>
                <InputLabel>Conexion</InputLabel>
                <Select
                  label="Conexion"
                  value={platformForm.connectionId || NO_STREAMING_CONNECTION}
                  onChange={event =>
                    setPlatformForm({
                      ...platformForm,
                      connectionId:
                        event.target.value === NO_STREAMING_CONNECTION
                          ? ""
                          : event.target.value,
                    })
                  }
                >
                  <MenuItem value={NO_STREAMING_CONNECTION}>Sin conexion</MenuItem>
                  {connections.map(connection => (
                    <MenuItem key={connection.id} value={connection.id}>{connection.name || connection.number || `Conexion ${connection.id}`}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2">Countdowns</Typography>
              {(platformForm.countdowns || []).map((countdown, index) => (
                <Grid container spacing={1} alignItems="flex-start" key={index}>
                  <Grid item xs={4} md={2}>
                    <TextField label="Antes" type="number" variant="outlined" margin="dense" fullWidth value={countdown.value} onChange={event => {
                      const countdowns = [...platformForm.countdowns];
                      countdowns[index] = { ...countdown, value: event.target.value };
                      setPlatformForm({ ...platformForm, countdowns });
                    }} />
                  </Grid>
                  <Grid item xs={8} md={3}>
                    <FormControl variant="outlined" margin="dense" fullWidth>
                      <InputLabel>Unidad</InputLabel>
                      <Select label="Unidad" value={countdown.unit || "days"} onChange={event => {
                        const countdowns = [...platformForm.countdowns];
                        countdowns[index] = { ...countdown, unit: event.target.value };
                        setPlatformForm({ ...platformForm, countdowns });
                      }}>
                        <MenuItem value="minutes">Minutos</MenuItem>
                        <MenuItem value="hours">Horas</MenuItem>
                        <MenuItem value="days">Dias</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={10} md={6}>
                    <FormControl variant="outlined" margin="dense" fullWidth>
                      <InputLabel>Secuencia</InputLabel>
                      <Select
                        label="Secuencia"
                        value={countdown.sequenceId ? String(countdown.sequenceId) : NO_COUNTDOWN_SEQUENCE}
                        onChange={event => {
                          if (event.target.value === "__create__") {
                            openCreateSequence(index);
                            return;
                          }
                          const countdowns = [...platformForm.countdowns];
                          countdowns[index] = {
                            ...countdown,
                            sequenceId:
                              event.target.value === NO_COUNTDOWN_SEQUENCE
                                ? ""
                                : event.target.value,
                          };
                          setPlatformForm({ ...platformForm, countdowns });
                        }}
                      >
                        <MenuItem value={NO_COUNTDOWN_SEQUENCE}>No ejecutar secuencia</MenuItem>
                        <MenuItem value="__create__">Crear secuencia...</MenuItem>
                        {sequences.map(sequence => (
                          <MenuItem key={sequence.id} value={String(sequence.id)}>
                            {formatSequenceOption(sequence)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      color="primary"
                      disabled={!countdown.sequenceId}
                      onClick={() => openEditSequence(index, countdown.sequenceId)}
                    >
                      Editar secuencia
                    </Button>
                  </Grid>
                  <Grid item xs={2} md={1}>
                    <IconButton size="small" onClick={() => setPlatformForm({ ...platformForm, countdowns: platformForm.countdowns.filter((_, i) => i !== index) })}>
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </Grid>
                </Grid>
              ))}
              <Button startIcon={<Add />} onClick={() => setPlatformForm({ ...platformForm, countdowns: [...platformForm.countdowns, { value: 1, unit: "days", sequenceId: "" }] })}>
                Agregar countdown
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlatformModalOpen(false)}>Cancelar</Button>
          <Button color="primary" variant="contained" onClick={savePlatform}>Guardar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkAssignModalOpen} onClose={() => setBulkAssignModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Asignar seleccion</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary">
                {Object.keys(selectedItems).length} elementos seleccionados
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                options={getContactOptions(bulkForm.contact)}
                getOptionLabel={getContactLabel}
                getOptionSelected={(option, value) => String(option?.id) === String(value?.id)}
                renderOption={renderContactOption}
                value={bulkForm.contact}
                onOpen={() => fetchContacts()}
                onChange={(_, value) => setBulkForm({ ...bulkForm, contact: value })}
                onInputChange={(_, value) => fetchContacts(value)}
                renderInput={params => <TextField {...params} label="Contacto" variant="outlined" margin="dense" />}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="Inicio" type="datetime-local" variant="outlined" margin="dense" fullWidth InputLabelProps={{ shrink: true }} value={bulkForm.startsAt} onChange={event => setBulkForm({ ...bulkForm, startsAt: event.target.value })} />
              <div className={classes.quickDateButtons}>
                {[30, 60, 90].map(days => (
                  <Button
                    key={days}
                    size="small"
                    variant="outlined"
                    onClick={() => setBulkForm({
                      ...bulkForm,
                      startsAt: bulkForm.startsAt || nowDateTimeInput(),
                      endsAt: addDaysToDateTimeInput(bulkForm.startsAt || nowDateTimeInput(), days),
                    })}
                  >
                    {days} dias
                  </Button>
                ))}
              </div>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="Finaliza" type="datetime-local" variant="outlined" margin="dense" fullWidth InputLabelProps={{ shrink: true }} value={bulkForm.endsAt} onChange={event => setBulkForm({ ...bulkForm, endsAt: event.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkAssignModalOpen(false)}>Cancelar</Button>
          <Button color="primary" variant="contained" onClick={saveBulkAssignment}>Asignar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(countdownAssignment)} onClose={() => setCountdownAssignment(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Countdowns programados</DialogTitle>
        <DialogContent dividers>
          <div className={classes.countdownSummary}>
            {(countdownAssignment?.reminders || []).length ? (
              (countdownAssignment.reminders || []).map((reminder, index) => (
                <Paper key={reminder.id || index} className={classes.countdownCard} elevation={0}>
                  <div className={classes.countdownCardHeader}>
                    <div>
                      <Typography variant="subtitle2">Countdown {index + 1}</Typography>
                      <Typography variant="body2" color="textSecondary">
                        {formatDateTime(reminder.scheduledAt, countdownAssignment?.connection?.timeZone)} - {reminder.chatbotSequence?.name || `Secuencia #${reminder.chatbotSequenceId || "-"}`} - {formatStreamingReminderStatus(reminder.status)}
                      </Typography>
                      {reminder.lastError && (
                        <Typography variant="caption" color="error">
                          {reminder.lastError}
                        </Typography>
                      )}
                    </div>
                    <div className={classes.countdownCardActions}>
                      <IconButton
                        size="small"
                        onClick={() => setConfirmDelete({ type: "countdown", item: reminder })}
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </div>
                  </div>
                </Paper>
              ))
            ) : (
              <Typography variant="body2" color="textSecondary">
                Esta asignacion no tiene countdowns programados.
              </Typography>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCountdownAssignment(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <ChatbotModal
        open={sequenceModalOpen}
        onClose={handleSequenceModalClose}
        onSave={handleSequenceSaved}
        sequenceId={sequenceModalId}
        defaultConnectionId={platformForm.connectionId}
        initialSequenceName="Recordatorio de streaming"
        initialSequenceCategory="Streaming"
        categories={["Streaming"]}
        extraVariables={streamingSequenceVariables}
      />
      <TicketStreamingModal
        modalOpen={Boolean(streamingModalTicket)}
        ticket={streamingModalTicket}
        onClose={() => {
          setStreamingModalTicket(null);
          fetchAccounts(selectedPlatformId);
        }}
      />
    </MainContainer>
  );
};

export default Streaming;
